import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { guid } from '@fry/lib/utils';

import {
  EditorEligibilityOperatorWithRule
} from '../editor-eligibility-operator-with-rule/editor-eligibility-operator-with-rule.interface';
import { EditorEligibilityTreeStore } from '../editor-eligibility-tree/editor-eligibility-tree.store';
import { EditorEligibilityRuleHasBookingItem } from '../editor-eligibility-rule/editor-eligibility-rule-has-booking-item.model';
import { EditorEligibilityRuleHasBookingOption } from '../editor-eligibility-rule/editor-eligibility-rule-has-booking-option.model';
import { EditorEligibilityRuleHasBookingType } from '../editor-eligibility-rule/editor-eligibility-rule-has-booking-type.model';
import { EditorEligibilityRuleHasEvent } from '../editor-eligibility-rule/editor-eligibility-rule-has-event.model';
import { EditorEligibilityRuleHasEventCount } from '../editor-eligibility-rule/editor-eligibility-rule-has-event-count.model';
import { EditorEligibilityRuleHasRelation } from '../editor-eligibility-rule/editor-eligibility-rule-has-relation.model';
import { EditorEligibilityRuleHasRole } from '../editor-eligibility-rule/editor-eligibility-rule-has-role.model';
import { EditorEligibilityRuleHasDateUserField } from '../editor-eligibility-rule/editor-eligibility-rule-has-date-user-field.model';
import { EditorEligibilityRuleHasDiscreteUserField } from '../editor-eligibility-rule/editor-eligibility-rule-has-discrete-user-field.model';
import { EditorEligibilityRuleHasStringUserField } from '../editor-eligibility-rule/editor-eligibility-rule-has-string-user-field.model';
import { EditorEligibilityRuleIsRawOperator } from '../editor-eligibility-rule/editor-eligibility-rule-is-raw-operator.model';


@Injectable()
export class EditorEligibilityOperatorEditStore {
  private readonly _replacingOperatorWithRule$ = new BehaviorSubject<EditorEligibilityOperatorWithRule>(null);
  private readonly _replacingOperatorWithRuleForm$ = new BehaviorSubject<FormGroup>(null);

  readonly replacingOperatorWithRule$ = this._replacingOperatorWithRule$.asObservable();
  readonly replacingOperatorWithRuleForm$ = this._replacingOperatorWithRuleForm$.asObservable();

  constructor(
    private fb: FormBuilder,
    private eligibilityTreeStore: EditorEligibilityTreeStore
  ) {
    this.eligibilityTreeStore.editingOperatorWithRule$.pipe(
      filter(editingOperatorWithRule => editingOperatorWithRule !== null)
    ).subscribe(editingOperatorWithRule =>
        this.initialize(editingOperatorWithRule)
    );
  }

  private get replacingOperatorWithRule(): EditorEligibilityOperatorWithRule {
    return this._replacingOperatorWithRule$.getValue();
  }

  private set replacingOperatorWithRule(value: EditorEligibilityOperatorWithRule) {
    this._replacingOperatorWithRule$.next(value);
  }

  private get replacingOperatorWithRuleForm(): FormGroup {
    return this._replacingOperatorWithRuleForm$.getValue();
  }

  private set replacingOperatorWithRuleForm(value: FormGroup) {
    this._replacingOperatorWithRuleForm$.next(value);
  }

  initialize(operatorWithRule: EditorEligibilityOperatorWithRule) {
    this.replacingOperatorWithRule = operatorWithRule;
    this.replacingOperatorWithRuleForm = this.buildForm(operatorWithRule);
  }

  terminate() {
    this.replacingOperatorWithRuleForm = null;
    this.replacingOperatorWithRule = null;
  }

  save() {
    const replacingOperatorWithRuleForm = this.replacingOperatorWithRuleForm;
    const replacingOperatorWithRule = {
      operator: this.replacingOperatorWithRule.operator,
      rule: this.replacingOperatorWithRule.rule
    };

    const replacingOperatorForm = replacingOperatorWithRuleForm.get('operator');
    if (replacingOperatorForm.dirty) {
      const operator = this.replacingOperatorWithRule.operator.createCopy();

      if (replacingOperatorForm.get('name').dirty) {
        operator.name = replacingOperatorForm.value.name;
      }

      if (replacingOperatorForm.get('affirmative').dirty) {
        operator.affirmative = replacingOperatorForm.value.affirmative;
      }

      const replacingOperatorFunctionForm = replacingOperatorForm.get('function');
      if (replacingOperatorFunctionForm !== null && replacingOperatorFunctionForm.dirty) {
        operator.function = replacingOperatorForm.value.function;
      }

      const replacingOperatorHideNestedDescriptionsForm = replacingOperatorForm.get('hideNestedDescriptions');
      if (replacingOperatorHideNestedDescriptionsForm !== null && replacingOperatorHideNestedDescriptionsForm.dirty) {
        operator.hideNestedDescriptions = replacingOperatorForm.value.hideNestedDescriptions;
      }

      replacingOperatorWithRule.operator = operator;
    }

    const replacingRuleForm = replacingOperatorWithRuleForm.get('rule');
    if (replacingRuleForm !== null && replacingRuleForm.dirty) {
      let rule = null;

      switch (replacingRuleForm.get('ruleClassName').value) {
        case 'HasRole':
          rule = new EditorEligibilityRuleHasRole({
            id: guid(),
            ...replacingRuleForm.get('hasRole').value
          });
          break;
        case 'HasRelation':
          rule = new EditorEligibilityRuleHasRelation({
            id: guid(),
            ...replacingRuleForm.get('hasRelation').value
          });
          break;
        case 'HasBookingItem':
          rule = new EditorEligibilityRuleHasBookingItem({
            id: guid(),
            ...replacingRuleForm.get('hasBookingItem').value,
            // states: replacingRuleForm.get('hasBookingItem').get('stateNamesString').value.split(',').map(state => state.trim())
          });
          break;
        case 'HasBookingOption':
          rule = new EditorEligibilityRuleHasBookingOption({
            id: guid(),
            ...replacingRuleForm.get('hasBookingOption').value,
            // states: replacingRuleForm.get('hasBookingOption').get('stateNamesString').value.split(',').map(state => state.trim())
          });
          break;
        case 'HasBookingType':
          rule = new EditorEligibilityRuleHasBookingType({
            id: guid(),
            ...replacingRuleForm.get('hasBookingType').value,
            // states: replacingRuleForm.get('hasBookingType').get('stateNamesString').value.split(',').map(state => state.trim())
          });
          break;
        case 'HasEvent':
          rule = new EditorEligibilityRuleHasEvent({
            id: guid(),
            ...replacingRuleForm.get('hasEvent').value,
            blueprints: replacingRuleForm.get('hasEvent').get('blueprints').value
              .map(blueprint => ({ id: blueprint.id, categories: blueprint.categories })),
            relativeStart: {
              years: replacingRuleForm.get('hasEvent').get('relativeStartYears').value,
              months: replacingRuleForm.get('hasEvent').get('relativeStartMonths').value,
              days: replacingRuleForm.get('hasEvent').get('relativeStartDays').value,
            }
          });
          break;
        case 'HasEventCount':
          rule = new EditorEligibilityRuleHasEventCount({
            id: guid(),
            ...replacingRuleForm.get('hasEventCount').value,
            blueprints: replacingRuleForm.get('hasEventCount').get('blueprints').value
              .map(blueprint => ({ id: blueprint.id, categories: blueprint.categories })),
            relativeStart: {
              years: replacingRuleForm.get('hasEventCount').get('relativeStartYears').value,
              months: replacingRuleForm.get('hasEventCount').get('relativeStartMonths').value,
              days: replacingRuleForm.get('hasEventCount').get('relativeStartDays').value,
            }
          });
          break;
        case 'HasDateUserField':
          rule = new EditorEligibilityRuleHasDateUserField({
            id: guid(),
            ...replacingRuleForm.get('hasDateUserField').value,
          });
          break;
        case 'HasDiscreteUserField':
          rule = new EditorEligibilityRuleHasDiscreteUserField({
            id: guid(),
            ...replacingRuleForm.get('hasDiscreteUserField').value,
          });
          break;
        case 'HasStringUserField':
          rule = new EditorEligibilityRuleHasStringUserField({
            id: guid(),
            ...replacingRuleForm.get('hasStringUserField').value,
          });
          break;
        case 'IsRawOperator':
          rule = new EditorEligibilityRuleIsRawOperator({
            id: guid(),
            ...replacingRuleForm.get('isRawOperator').value,
          });
          break;
      }

      replacingOperatorWithRule.rule = rule;
    }

    this.eligibilityTreeStore.replaceOperatorWithRule(replacingOperatorWithRule.operator.id, replacingOperatorWithRule);
  }

  private buildForm(operatorWithRule: EditorEligibilityOperatorWithRule): FormGroup {
    const controlsConfig: any = { };
    const operatorControlsConfig: any = {
      name: this.fb.control(operatorWithRule.operator.name, Validators.required),
      affirmative: this.fb.control(operatorWithRule.operator.affirmative)
    };

    if (operatorWithRule.operator.hasRule()) {
      controlsConfig.rule = this.fb.group({
        ruleClassName: operatorWithRule.rule === null ? null : operatorWithRule.rule.className,
        hasRole: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasRole'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasRole.getEmptyControlConfig()
        ),
        hasRelation: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasRelation'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasRelation.getEmptyControlConfig()
        ),
        hasBookingItem: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasBookingItem'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasBookingItem.getEmptyControlConfig()
        ),
        hasBookingOption: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasBookingOption'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasBookingOption.getEmptyControlConfig()
        ),
        hasBookingType: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasBookingType'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasBookingType.getEmptyControlConfig()
        ),
        hasEvent: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasEvent'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasEvent.getEmptyControlConfig()
        ),
        hasEventCount: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasEventCount'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasEventCount.getEmptyControlConfig()
        ),
        hasDateUserField: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasDateUserField'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasDateUserField.getEmptyControlConfig()
        ),
        hasDiscreteUserField: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasDiscreteUserField'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasDiscreteUserField.getEmptyControlConfig()
        ),
        hasStringUserField: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'HasStringUserField'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleHasStringUserField.getEmptyControlConfig()
        ),
        isRawOperator: this.fb.group(
          operatorWithRule.rule !== null && operatorWithRule.rule.className === 'IsRawOperator'
            ? operatorWithRule.rule.getControlsConfig()
            : EditorEligibilityRuleIsRawOperator.getEmptyControlConfig()
        )
      });
    } else {
      operatorControlsConfig.function = this.fb.control(operatorWithRule.operator.function);
      operatorControlsConfig.hideNestedDescriptions = this.fb.control(operatorWithRule.operator.hideNestedDescriptions);
    }

    controlsConfig.operator = this.fb.group(operatorControlsConfig);
    return this.fb.group(controlsConfig);
  }
}
